import * as React from 'react'
// TODO(icons): approx — was Lucide OctagonXIcon, TriangleAlertIcon
import { InfoIcon, ErrorIcon, WarningIcon, CloseIcon } from '@/src/icons'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/registry/reapit/lib/utils'
import { Flex } from '@/registry/reapit/ui/flex'

/*
 * Alert Banner — DS alignment (Phase 3C)
 *
 * The DS specifies full-width, no rounded corners (lives at the page edge).
 * Icon size: DS icon-md = 20px (size-5).
 * Variants:  info / warning / error — colours via --alert-* aliases → Phase 1 tokens.
 * On SM and below the action group wraps below the message (handled via flex-wrap).
 *
 * DS page: 8395323
 */
const bannerVariants = cva('w-full border-b px-4 py-3 text-sm', {
  variants: {
    variant: {
      info: 'border-[var(--alert-info-border)] bg-[var(--alert-info-bg)] text-[var(--alert-info-foreground)] [&_[data-slot=alert-banner-icon]]:text-[var(--alert-info-icon)]',
      error:
        'border-[var(--alert-error-border)] bg-[var(--alert-error-bg)] text-[var(--alert-error-foreground)] [&_[data-slot=alert-banner-icon]]:text-[var(--alert-error-icon)]',
      warning:
        'border-[var(--alert-warning-border)] bg-[var(--alert-warning-bg)] text-[var(--alert-warning-foreground)] [&_[data-slot=alert-banner-icon]]:text-[var(--alert-warning-icon)]',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
})

const iconMap = {
  info: InfoIcon,
  error: ErrorIcon,
  warning: WarningIcon,
}

type AlertBannerProps = React.ComponentProps<'div'> &
  VariantProps<typeof bannerVariants> & {
    dismissible?: boolean
    onDismiss?: () => void
    actions?: React.ReactNode
  }

function AlertBanner({
  className,
  variant = 'info',
  dismissible = false,
  onDismiss,
  actions,
  children,
  ...props
}: AlertBannerProps) {
  const Icon = iconMap[variant ?? 'info']

  return (
    <div
      data-slot="alert-banner"
      data-variant={variant}
      role="alert"
      className={cn(bannerVariants({ variant }), className)}
      {...props}
    >
      <Flex align="start" gap={3}>
        {/* DS icon-md = 20px */}
        <Icon data-slot="alert-banner-icon" className="mt-0.5 size-5 shrink-0" aria-hidden="true" />

        {/*
         * Inner row: message + optional actions flex-wrap so that on narrow
         * viewports (XS–SM) the button group drops below the message text.
         * DS breakpoint variant: actions below message at SM and below.
         */}
        <Flex align="start" gap={4} className="flex-1 flex-wrap">
          <div data-slot="alert-banner-message" className="flex-1">
            {children}
          </div>

          {actions && (
            <div data-slot="alert-banner-actions" className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </Flex>

        {dismissible && (
          <button
            data-slot="alert-banner-dismiss"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="ml-auto shrink-0 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <CloseIcon size="sm" />
          </button>
        )}
      </Flex>
    </div>
  )
}

function AlertBannerActions({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="alert-banner-actions" className={cn('flex items-center gap-2', className)} {...props} />
}

export { AlertBanner, AlertBannerActions }
export type { AlertBannerProps }
